<template>
  <div
    class="wt-files-dropzone"
    :class="stateClasses"
    @dragover="onDragOver"
    @dragenter="onDragEnter"
    @dragleave="onDragLeave"
    @dragend="onDragEnd"
    v-on="onFileDropped ? { drop: handleDrop } : {}"
  >
    <slot name="border">
      <div class="wt-files-dropzone-border"></div>
    </slot>
    <span class="wt-files-dropzone-drop-indicator">
      <slot name="indicator">
        <i
          class="wticon wticon-cloud-upload wt-files-dropzone-drop-indicator-icon"
        />
        <span class="wt-files-dropzone-drop-indicator-text"
          >Drop file here</span
        >
      </slot>
    </span>
    <slot />
  </div>
</template>

<script>
import app from 'app';

/**
   *  wt-files-dropzone Vue component
   *

   *
   *  props:
   *        * disabled: should dropzone be enabled (if not, there's return in each event)
   *        * show - indicates that there is a dragging on window or parent element
   *        * accept: array of accepted types
   *        * numberOfFiles: number of permitted files to drop
   *        * onFileDropped - callback when a file dropped (after validation)
   *        * onError - callback when an error happen
   */
export default {
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },

    show: {
      type: Boolean,
      default: false,
    },

    accept: {
      type: Array,
      default: () => [],
    },

    numberOfFiles: {
      type: Number,
      default: Infinity,
    },

    onFileDropped: {
      type: Function,
      require: false,
    },

    onError: {
      type: Function,
      require: true,
    },
  },

  created() {
    this.isWrapper = !!this.$slots.default;
  },

  data: function () {
    return {
      active: false,
      isWrapper: true,
    };
  },

  computed: {
    stateClasses() {
      return [
        `-mode-${this.isWrapper ? 'absolute' : 'static'}`,
        {
          '-visible': this.isVisible,
          '-active': this.isActive,
        },
      ];
    },

    isVisible() {
      return !this.disabled && this.show;
    },

    isActive() {
      return this.isVisible && this.active;
    },
  },

  watch: {
    show() {
      if (!this.show) {
        this.active = false;
      }
    },
  },

  methods: {
    isValidEvent(e) {
      const types = e.dataTransfer.types;
      return types && types.includes('Files');
    },

    abortEvent(e) {
      return this.disabled || !this.isValidEvent(e);
    },

    onDrop: function (e) {
      e.preventDefault();
      e.stopPropagation();

      this.active = false;

      if (this.abortEvent(e)) {
        return false;
      }

      const files = e.dataTransfer.files;
      return this.isValid(files);
    },

    handleDrop(e) {
      if (this.onDrop(e) && _.isFunction(this.onFileDropped)) {
        const files = e.dataTransfer.files;
        this.onFileDropped(files, e);
      }
    },

    onDragOver: function (e) {
      e.preventDefault();
      e.stopPropagation();

      if (this.abortEvent(e)) {
        return;
      }

      this.errorText = null;

      e.dataTransfer.dropEffect = 'copy';
    },

    onDragEnter: function (e) {
      e.preventDefault();
      e.stopPropagation();

      if (this.abortEvent(e)) {
        return;
      }

      this.active = true;
    },

    onDragLeave: function (e) {
      e.preventDefault();
      e.stopPropagation();

      if (this.abortEvent(e)) {
        return;
      }

      this.active = this.$el.contains(
        document.elementFromPoint(e.clientX, e.clientY)
      );
    },

    onDragEnd: function (e) {
      e.preventDefault();
      e.stopPropagation();

      if (this.abortEvent(e)) {
        return;
      }

      this.active = false;
    },

    isValid: function (files) {
      let isValid = true;
      let errorText;

      // turn's files like array to actual array for use of array methods
      const filesArr = Array.from(files);

      if (!filesArr || !filesArr.length) {
        isValid = false;

        errorText = 'No Files!';
      } else if (filesArr.length > this.numberOfFiles) {
        isValid = false;

        if (this.numberOfFiles === 1) {
          errorText = 'You can upload a single file only';
        } else {
          errorText = `You can upload ${this.numberOfFiles} files only`;
        }
      } else if (
        this.accept.length &&
        filesArr.every((file) => !this.accept.includes(file.type))
      ) {
        isValid = false;
        errorText = `Upload supported audio formats: ${app.DEFAULT.SUPPORTED_AUDIO_EXTENSIONS}`;
      }

      if (errorText) {
        this.onError(errorText);
      }

      return isValid;
    },
  },
};
</script>
